import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { GoogleLogo, SignOut, CaretDown, ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function SignInButton() {
  const { user, loading, signIn, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (loading) {
    return <span data-testid="auth-loading" className="w-16 h-6 bg-zinc-100 animate-pulse" />;
  }

  if (!user) {
    return (
      <button
        type="button"
        onClick={signIn}
        data-testid="sign-in-button"
        className="inline-flex items-center gap-1.5 border border-black bg-black text-white px-3 py-1.5 font-mono-plex text-[10px] font-semibold hover:bg-zinc-800 transition-colors"
      >
        <GoogleLogo weight="bold" className="w-3.5 h-3.5" />
        SIGN IN
      </button>
    );
  }

  const initials = (user.name || user.email || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="user-menu-button"
        className="inline-flex items-center gap-2 border border-zinc-300 bg-white px-1.5 py-1 hover:border-zinc-900 transition-colors"
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name || user.email}
            className="w-5 h-5 rounded-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[9px] font-bold">
            {initials || "U"}
          </span>
        )}
        <span className="font-mono-plex text-[10px] font-semibold text-zinc-800 max-w-[120px] truncate normal-case tracking-normal">
          {user.name || user.email}
        </span>
        <CaretDown weight="bold" className={`w-3 h-3 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="user-menu"
          className="absolute right-0 top-full mt-1 w-56 border border-zinc-900 bg-white shadow-[3px_3px_0px_rgba(0,0,0,1)] z-50"
        >
          <div className="px-3 py-2 border-b border-zinc-200">
            <div className="text-[12px] font-semibold text-zinc-950 truncate">{user.name || "TrueStream user"}</div>
            <div className="text-[11px] text-zinc-500 truncate">{user.email}</div>
          </div>
          {user.is_admin && (
            <Link
              to="/admin"
              onClick={() => setOpen(false)}
              data-testid="user-menu-admin"
              className="w-full text-left px-3 py-2 flex items-center gap-2 font-mono-plex text-[10px] font-semibold text-emerald-800 hover:bg-emerald-50 border-b border-zinc-200"
            >
              <ShieldCheck weight="fill" className="w-3.5 h-3.5" />
              ADMIN DASHBOARD
            </Link>
          )}
          <button
            type="button"
            onClick={() => { setOpen(false); signOut(); }}
            data-testid="user-menu-signout"
            className="w-full text-left px-3 py-2 flex items-center gap-2 font-mono-plex text-[10px] font-semibold text-zinc-800 hover:bg-zinc-50"
          >
            <SignOut weight="bold" className="w-3.5 h-3.5" />
            SIGN OUT
          </button>
        </div>
      )}
    </div>
  );
}
